import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, useLocation } from "@tanstack/react-router";
import { FUNCTIE_HASH } from "./features/banen/functies";
import { SECTIES, type Sectie } from "./features/beheer/signals";
import { InloggenPage } from "./routes/InloggenPage";
import { NotFound } from "./routes/NotFound";
import { SignInRefusedPage } from "./routes/SignInRefusedPage";

// A chunk per screen keeps the first load small; the service worker precaches every chunk for offline use.
const BanenPage = lazyRouteComponent(() => import("./routes/BanenPage"), "BanenPage");
const BaanPage = lazyRouteComponent(() => import("./routes/BaanPage"), "BaanPage");
const JurylijstPage = lazyRouteComponent(() => import("./routes/JurylijstPage"), "JurylijstPage");
const BeheerPage = lazyRouteComponent(() => import("./routes/BeheerPage"), "BeheerPage");
const OverridesPage = lazyRouteComponent(() => import("./routes/OverridesPage"), "OverridesPage");

// Code-based: nine routes do not earn a generated route tree.
const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

// A stale or hand-typed value falls back to nothing chosen rather than throwing. Plain checks, not zod: zod adds some 80 kB to the chunk every page loads first.
type Search = Record<string, unknown>;
const text = (value: unknown) => (typeof value === "string" ? value : undefined);
const int = (value: unknown) => ((typeof value === "number" || typeof value === "string") && /^-?\d+$/.test(String(value)) ? Number(value) : undefined);

const banenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (s: Search): { q?: string; date?: string; klasse?: string; level?: string; region?: string; jbnl?: string } => ({
    q: text(s.q),
    date: text(s.date),
    klasse: text(s.klasse),
    level: text(s.level),
    region: text(s.region),
    jbnl: text(s.jbnl),
  }),
  component: function Banen() {
    const search = banenRoute.useSearch();
    const navigate = banenRoute.useNavigate();
    // Replace: narrowing a list is a change within the screen, not a stop to walk back through.
    return <BanenPage search={search} onSearch={(next) => void navigate({ search: next, replace: true, resetScroll: false })} />;
  },
});

const baanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/banen/$baanId",
  component: function Baan() {
    const { baanId } = baanRoute.useParams();
    return <BaanPage baanId={Number(baanId)} />;
  },
});

const jurylijstRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/banen/$baanId/jurylijsten/$lijst",
  // namens: the contact id of the waarnemer a beheerder fills in for.
  validateSearch: (s: Search): { ploeg?: number; namens?: number } => ({ ploeg: int(s.ploeg), namens: int(s.namens) }),
  component: function Jurylijst() {
    const { baanId, lijst } = jurylijstRoute.useParams();
    const { ploeg, namens } = jurylijstRoute.useSearch();
    // `#functie-16`: on lijst `alles`, the functie to scroll to. Kept on a ploeg switch, as the vragen show only once a ploeg is chosen.
    const hash = useLocation({ select: (location) => location.hash });
    const navigate = jurylijstRoute.useNavigate();
    return (
      <JurylijstPage
        baanId={Number(baanId)}
        lijst={lijst}
        ploeg={ploeg}
        namens={namens}
        functie={hash.startsWith(FUNCTIE_HASH) ? hash.slice(FUNCTIE_HASH.length) : undefined}
        onPloeg={(uitslagId) => void navigate({ search: { ploeg: uitslagId, namens }, hash: true, resetScroll: false })}
      />
    );
  },
});

// The chosen section, ploeg and vraag live in the URL, so a reload or the back gesture keeps the selection.
const beheerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/banen/$baanId/beheer",
  validateSearch: (s: Search): { ploeg?: number; sectie?: Sectie } => ({ ploeg: int(s.ploeg), sectie: SECTIES.find((sectie) => sectie === s.sectie) }),
  component: function Beheer() {
    const { baanId } = beheerRoute.useParams();
    const { ploeg, sectie = "overzicht" } = beheerRoute.useSearch();
    return <BeheerPage baanId={Number(baanId)} ploeg={ploeg} sectie={sectie} />;
  },
});

const overridesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/banen/$baanId/beheer/ploegen/$uitslagId",
  validateSearch: (s: Search): { vraag?: string } => ({ vraag: text(s.vraag) }),
  component: function Overrides() {
    const { baanId, uitslagId } = overridesRoute.useParams();
    const { vraag } = overridesRoute.useSearch();
    const navigate = overridesRoute.useNavigate();
    return (
      <OverridesPage
        baanId={Number(baanId)}
        uitslagId={Number(uitslagId)}
        vraag={vraag}
        onVraag={(slot) => void navigate({ search: { vraag: slot }, resetScroll: false })}
      />
    );
  },
});

const inloggenRoute = createRoute({ getParentRoute: () => rootRoute, path: "/inloggen", component: InloggenPage });

// Where the Api sends a Google or Microsoft sign-in that got no session.
const onbekendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inloggen/onbekend",
  component: () => <SignInRefusedPage reason="onbekend" />,
});
const misluktRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inloggen/mislukt",
  component: () => <SignInRefusedPage reason="mislukt" />,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([banenRoute, baanRoute, jurylijstRoute, beheerRoute, overridesRoute, inloggenRoute, onbekendRoute, misluktRoute]),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
